// MarineMapGoogle.jsx — Google Maps (no Google geocoder/places billing)
// - Port-accurate ships via FREE batch geocoding (Photon/OSM) + local cache
// - ~5 miles offshore placement, green halo, ref-id badges, edit/repost/delete
// - Cargos with route polyline, draggable info panel
// - Mobile bottom sheet sidebar (xm screens); Desktop left sidebar (hidden on small)
// - Loading logic for ships + cargos (like CargoMap)
// - Ship clustering (cluster when zoomed out, side-by-side when zoomed in, no blink)
// - Cluster badge = round circle on top-right of icon, only if >1 ship in same port

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  GoogleMap,
  useJsApiLoader,
  MarkerF,
  PolylineF,
} from "@react-google-maps/api";

import config from "../function/config";
import HomeNav from "./Navbars/HomeNav";
import AdminNav from "./Admin/AdminHome/AdminNav";
import Usernav from "./UserHome/Usernav";
import CargoIcon from "../images/CargoIcon.png";
import ShipIconImg from "../images/pin5.png";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import NewHomeNav from "./Navbars/NewHomeNav";
import NewAdminNav from "./Admin/AdminHome/NewAdminNav";

/* ======================= DPR-aware bitmap helper ======================= */
const DPR = Math.min(window.devicePixelRatio || 1, 2);
function makeIconHD(url, displayW, displayH, anchorX, anchorY) {
  const w = Math.round(displayW);
  const h = Math.round(displayH);
  const natW = Math.round(displayW * DPR);
  const natH = Math.round(displayH * DPR);

  return {
    url,
    // SizeLiteral
    size: { width: natW, height: natH },
    scaledSize: { width: w, height: h },
    // PointLiteral
    origin: { x: 0, y: 0 },
    anchor: {
      x: anchorX != null ? anchorX : Math.round(w / 2),
      y: anchorY != null ? anchorY : Math.round(h - 6),
    },
  };
}


/* ========= Small rectangle “badge” icon for ship/cargo reference IDs ========= */
const REF_BADGE_MIN_ZOOM = 10;
function makeRefBadgeIcon(
  text,
  {
    fontSize = 12,
    hPad = 8,
    vPad = 4,
    radius = 6,
    bg = "#111827",
    fg = "#FFFFFF",
    yOffset = 22,
  } = {}
) {
  const estChar = Math.max(1, String(text || "").length);
  const charW = Math.round(fontSize * 0.62);
  const textW = estChar * charW;
  const w = textW + hPad * 2;
  const h = fontSize + vPad * 2;

  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
  <rect x="0" y="0" rx="${radius}" ry="${radius}" width="${w}" height="${h}" fill="${bg}" opacity="0.92"/>
  <text x="${w / 2}" y="${Math.round(
    h / 2 + fontSize / 2 - 2
  )}" font-family="Inter,system-ui,Segoe UI,Roboto,Helvetica,Arial"
        font-size="${fontSize}" font-weight="700" text-anchor="middle" fill="${fg}">${(text || "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")}</text>
</svg>`.trim();

  const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

  return {
    url,
    size: { width: w, height: h },
    scaledSize: { width: w, height: h },
    anchor: { x: Math.round(w / 2), y: h + yOffset },
  };
}

/* ========= Small round circle badge for CLUSTERS (higher above pin) ========= */
function makeClusterCountIcon(count) {
  const text = String(count);

  const r = 10;
  const pad = 16;
  const extraBottom = 18;
  const w = r * 2 + pad * 2;
  const h = r * 2 + pad * 2 + extraBottom;
  const cx = w / 2;
  const cy = r + pad;

  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
  <circle cx="${cx}" cy="${cy}" r="${r}" fill="#F44336" stroke="#FFFFFF" stroke-width="2" />
  <text x="${cx}" y="${cy + 4}"
        font-family="Inter,system-ui,Segoe UI,Roboto,Helvetica,Arial"
        font-size="12" font-weight="700"
        text-anchor="middle" fill="#FFFFFF">
    ${text}
  </text>
</svg>`.trim();

  const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

  return {
    url,
    size: { width: w, height: h },
    scaledSize: { width: w, height: h },
    anchor: { x: w / 2, y: h - 2 },
  };
}


// ---------- label visibility helpers ----------
const SHIP_LABEL_MIN_ZOOM = 9;
const MAX_LABELS_PER_GROUP = 2;
const GROUP_MAX_FOR_LABELS = 6;
const CARGO_LABEL_MIN_ZOOM = 9;

function shouldShowShipLabel({
  isSelected,
  isHovered,
  mapZoom,
  groupCount,
  indexInGroup,
}) {
  if (isSelected || isHovered) return true;
  if ((mapZoom || 5) < SHIP_LABEL_MIN_ZOOM) return false;
  if (groupCount > GROUP_MAX_FOR_LABELS) return false;
  return indexInGroup < MAX_LABELS_PER_GROUP;
}
function shouldShowCargoLabel({
  isSelected,
  isHovered,
  mapZoom,
  groupCount = 1,
  indexInGroup = 0,
}) {
  if (isSelected || isHovered) return true;
  if ((mapZoom || 5) < CARGO_LABEL_MIN_ZOOM) return false;
  if (groupCount > GROUP_MAX_FOR_LABELS) return false;
  return indexInGroup < MAX_LABELS_PER_GROUP;
}

const shipRefText = (s) => {
  const code = (s?.ship_code ?? "").toString().trim() || "-";
  const id = (s?.id ?? s?._id ?? "").toString().trim() || "-";
  return `${code} - ${id}`;
};
const pickId = (row) =>
  String(row?.ship_code || row?.id || row?._id || "").trim();
const short = (s, n = 16) => {
  const t = String(s || "").trim();
  return t.length > n ? t.slice(0, n - 1) + "…" : t;
};
function cargoLabelText(c) {
  const id = pickId(c) || "-";
  const ct = short(c?.type_of_cargo || "Cargo", 18);
  return `${id} · ${ct}`;
}
const cargoRefText = (c) => {
  const code = "Ref ID";
  const id = (c?.id ?? c?._id ?? "").toString().trim() || "-";
  return `${code} - ${id}`;
};

/* ========= Helper: get ship timestamp (for "latest" ship selection) ========= */
function getShipTimestamp(x) {
  const d =
    x?.created_at ||
    x?.createdAt ||
    x?.created_on ||
    x?.updated_at ||
    x?.timestamp ||
    x?.time ||
    x?.date;
  const n = d ? +new Date(d) : 0;
  return Number.isFinite(n) ? n : 0;
}

(function preloadPins() {
  [ShipIconImg, CargoIcon].forEach((url) => {
    const img = new Image();
    img.src = url;
  });
})();

/* ======================= Draggable panel (cargo) ======================= */
function DraggablePanel({
  id = "drag-panel",
  initial = { x: 16, y: 260 },
  children,
  onClose,
  className = "",
  title,
  closeOnOutside = true,
}) {
  const wrapRef = useRef(null);
  const [pos, setPos] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(`panel-pos:${id}`)) || initial;
    } catch {
      return initial;
    }
  });
  const drag = useRef({ active: false, sx: 0, sy: 0, bx: pos.x, by: pos.y });
  const keepInBounds = useCallback((x, y) => {
    const el = wrapRef.current;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const rect = el?.getBoundingClientRect?.() || { width: 360, height: 320 };
    const pad = 8;
    return {
      x: Math.min(Math.max(x, pad), Math.max(pad, vw - rect.width - pad)),
      y: Math.min(Math.max(y, pad), Math.max(pad, vh - rect.height - pad)),
    };
  }, []);
  useEffect(() => {
    sessionStorage.setItem(`panel-pos:${id}`, JSON.stringify(pos));
  }, [id, pos]);
  useEffect(() => {
    const onResize = () => setPos((p) => keepInBounds(p.x, p.y));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [keepInBounds]);

  useEffect(() => {
    if (!closeOnOutside || !onClose) return;
    const handler = (e) => {
      const el = wrapRef.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      if (drag.current.active) return;
      onClose();
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("pointerdown", handler, { capture: true });
    window.addEventListener("mousedown", handler, { capture: true });
    window.addEventListener("touchstart", handler, { capture: true });
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", handler, { capture: true });
      window.removeEventListener("mousedown", handler, { capture: true });
      window.removeEventListener("touchstart", handler, { capture: true });
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [closeOnOutside, onClose]);

  const start = (cx, cy) => {
    drag.current = { active: true, sx: cx, sy: cy, bx: pos.x, by: pos.y };
    document.body.style.userSelect = "none";
  };
  const move = (cx, cy) => {
    if (!drag.current.active) return;
    const dx = cx - drag.current.sx;
    const dy = cy - drag.current.sy;
    setPos(keepInBounds(drag.current.bx + dx, drag.current.by + dy));
  };
  const end = () => {
    drag.current.active = false;
    document.body.style.userSelect = "";
  };
  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    start(e.clientX, e.clientY);
    window.addEventListener("pointermove", onPointerMove, { passive: false });
    window.addEventListener("pointerup", onPointerUp, { passive: false });
  };
  const onPointerMove = (e) => {
    e.preventDefault();
    move(e.clientX, e.clientY);
  };
  const onPointerUp = (e) => {
    e.preventDefault();
    end();
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
  };
  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    e.preventDefault();
    e.stopPropagation();
    start(t.clientX, t.clientY);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd, { passive: false });
  };
  const onTouchMove = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    e.preventDefault();
    move(t.clientX, t.clientY);
  };
  const onTouchEnd = (e) => {
    e.preventDefault();
    end();
    window.removeEventListener("touchmove", onTouchMove);
    window.removeEventListener("touchend", onTouchEnd);
  };

  return (
    <div
      ref={wrapRef}
      className={`fixed z-[1100] pointer-events-auto ${className}`}
      style={{
        left: 0,
        top: 0,
        transform: `translate(${pos.x}px, ${pos.y}px)`,
        willChange: "transform",
      }}
      onPointerDown={onPointerDown}
      onTouchStart={onTouchStart}
    >
      <div
        className="rounded-2xl overflow-hidden backdrop-blur-md bg-white/90 dark:bg-slate-800/85 border border-white/50 shadow-2xl"
        style={{ touchAction: "none" }}
      >
        <div className="flex items-center justify-between px-3 py-2 bg-[#d1a460] text-black select-none">
          <div className="font-bold text-sm truncate max-w-[260px]">
            {title || "Panel"}
          </div>
          {onClose && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="text-black/80 hover:text-black"
              aria-label="Close"
              title="Close"
            >
              ✕
            </button>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}

/* ---------- misc utils ---------- */
function timeAgo(d) {
  const t = d ? +new Date(d) : 0;
  if (!Number.isFinite(t)) return "";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}
const USER_POS_KEY = "mariniz:lastUserPos";
const DID_FLY_KEY = "mariniz:didFlyThisPage";
const readLastUserPos = () => {
  try {
    const raw =
      sessionStorage.getItem(USER_POS_KEY) ||
      localStorage.getItem(USER_POS_KEY);
    if (!raw) return null;
    const { lat, lon } = JSON.parse(raw);
    if (typeof lat === "number" && typeof lon === "number")
      return { lat, lng: lon };
  } catch {}
  return null;
};
const writeLastUserPos = (pos) => {
  try {
    const payload = JSON.stringify({
      lat: pos.lat,
      lon: pos.lng,
      t: Date.now(),
    });
    sessionStorage.setItem(USER_POS_KEY, payload);
    localStorage.setItem(USER_POS_KEY, payload);
  } catch {}
};

const INITIAL_CENTER = { lat: 20, lng: 0 };
const GMAPS_API_KEY = "AIzaSyDcu35S12sB5ryz4Ewd3hkZ745qASnKxMM";

const GMAPS_LIBRARIES = []; // IMPORTANT: same array used everywhere

export const GMAPS_LOADER_OPTIONS = {
  id: "gmaps-marine",           // <-- use this ID everywhere
  googleMapsApiKey: GMAPS_API_KEY,
  libraries: GMAPS_LIBRARIES,   // <-- same reference (not a new [])
  language: "en",
  region: "US",
};
const norm = (s = "") =>
  s.trim().toLowerCase().replace(/\s+/g, " ").replace(/,+/g, ",");
const isVisible = (v) =>
  !(v === false || v === 0 || v === "0" || v === "false" || v === "False");

/* ====== Area aliases & overrides ====== */
const AREA_ALIASES = {
  pg: "persian gulf",
  ag: "arabian gulf",
  gcc: "arabian gulf",
  bsea: "black sea",
  med: "mediterranean sea",
  "e med": "eastern mediterranean",
  "w med": "western mediterranean",
  wci: "west coast india",
  eci: "east coast india",
  kandla: "mundra port",
  deendayal: "kandla",
  fe: "far east",
  seasia: "southeast asia",
  "se asia": "southeast asia",
  "ne asia": "northeast asia",
  "n china": "north china",
  "s china": "south china",
  usec: "us east coast",
  uswc: "us west coast",
  usg: "us gulf",
  cont: "northwest europe",
  "jebel ali": "jabal ali",
};
const AREA_COORD_OVERRIDES = {
  "persian gulf": [25.5, 54.5],
  "arabian gulf": [25.5, 54.5],
  "us gulf": [28.4, -90.0],
  "us east coast": [36.5, -75.2],
  "us west coast": [34.1, -119.2],
  "northwest europe": [52.0, 3.8],
  "black sea": [42.0, 35.0],
  "mediterranean sea": [36.2, 17.0],
  "eastern mediterranean": [34.0, 25.0],
  "western mediterranean": [39.0, 5.0],
  "far east": [30.0, 123.0],
  "southeast asia": [2.5, 104.0],
  "northeast asia": [35.0, 135.0],
  "north china": [38.9, 118.0],
  "south china": [22.3, 114.2],
  "west coast india": [21.6, 69.7],
  "east coast india": [17.7, 83.3],
  "jabal ali": [25.0, 55.07],
  dubai: [25.25, 55.28],
  fujairah: [25.12, 56.36],
  "abu dhabi": [24.45, 54.38],
  "ras al khaimah": [25.77, 55.95],
  kandla: [22.94, 69.7],
  "mundra port": [22.73, 69.7],
  chittagong: [22.22, 91.79],
  singapore: [1.26, 103.83],
  rotterdam: [51.95, 4.14],
  antwerp: [51.28, 4.4],
  hamburg: [53.54, 9.98],
  piraeus: [37.94, 23.62],
};
const normAreaKey = (s) => {
  const k = norm(String(s || ""));
  return AREA_ALIASES[k] || k;
};
const applyAreaOverrideIfAny = (k, fallback) => {
  const ov = AREA_COORD_OVERRIDES[k];
  if (ov) return { lat: Number(ov[0]), lng: Number(ov[1]) };
  return fallback
    ? { lat: Number(fallback[0]), lng: Number(fallback[1]) }
    : undefined;
};
const getShipKey = (s) =>
  `${s?.id || s?._id || s?.reference_id || "noid"}::${normAreaKey(
    s?.open_area || ""
  )}`;

/* ======= Sharing helpers ======= */
const buildShipShareUrl = (shipKey) => {
  const { origin, pathname } = window.location;
  const url = new URL(origin + pathname);
  url.searchParams.set("ship", shipKey);
  return url.toString();
};
async function shareShipOnWhatsApp(shipKey, ship) {
  const url = buildShipShareUrl(shipKey);
  const title = `${ship?.vessel_type || "Ship"} for Charter`;
  const text = `Check this ${ship?.vessel_type || "ship"} open at ${
    ship?.open_area || "-"
  }.`;
  try {
    if (navigator.share) {
      await navigator.share({ title, text, url });
      return;
    }
  } catch (_) {}
  const wa = `https://wa.me/?text=${encodeURIComponent(
    `${title}\n${text}\n${url}`
  )}`;
  window.open(wa, "_blank", "noopener,noreferrer");
}

/* --------- geo helpers ---------- */
function moveByBearing(lat, lon, dist, bearing) {
  const R = 6371000,
    φ1 = (lat * Math.PI) / 180,
    λ1 = (lon * Math.PI) / 180,
    θ = (bearing * Math.PI) / 180,
    δ = dist / R;
  const sinφ1 = Math.sin(φ1),
    cosφ1 = Math.cos(φ1),
    sinδ = Math.sin(δ);
  const sinφ2 = sinφ1 * Math.cos(δ) + cosφ1 * sinδ * Math.cos(θ);
  const φ2 = Math.asin(sinφ2);
  const y = Math.sin(θ) * sinδ * cosφ1;
  const x = Math.cos(δ) - sinφ1 * sinφ2;
  const λ2 = λ1 + Math.atan2(y, x);
  return {
    lat: (φ2 * 180) / Math.PI,
    lng: (((λ2 * 180) / Math.PI + 540) % 360) - 180,
  };
}
const seaBearingOverrides = {
  dubai: 305,
  "jabal ali": 305,
  fujairah: 100,
  "abu dhabi": 130,
  kandla: 240,
  "mundra port": 240,
  chittagong: 220,
  singapore: 160,
  rotterdam: 300,
  antwerp: 300,
  hamburg: 315,
};
const FIVE_MILES_KM = 8.04672;
const seaDistanceOverridesKm = {
  dubai: 12,
  "jabal ali": 12,
  fujairah: 8,
  "abu dhabi": 75,
  kandla: 18,
  "mundra port": 18,
  chittagong: 25,
};
function pickSeaDistanceKm(areaName, fallback = FIVE_MILES_KM) {
  const k = normAreaKey(areaName || "");
  const first = k.split(",")[0];
  if (seaDistanceOverridesKm[first] != null)
    return seaDistanceOverridesKm[first];
  if (seaDistanceOverridesKm[k] != null) return seaDistanceOverridesKm[k];
  for (const key in seaDistanceOverridesKm) {
    if (first.includes(key) || k.includes(key))
      return seaDistanceOverridesKm[key];
  }
  return fallback;
}
function pickSeaBearing(areaName, lon) {
  const k = normAreaKey(areaName || "");
  const first = k.split(",")[0];
  if (seaBearingOverrides[first]) return seaBearingOverrides[first];
  if (seaBearingOverrides[k]) return seaBearingOverrides[k];
  for (const key in seaBearingOverrides) {
    if (first.includes(key) || k.includes(key)) return seaBearingOverrides[key];
  }
  return lon >= 0 ? 135 : 225;
}
function sideBySidePositions(
  baseLat,
  baseLon,
  areaName,
  count,
  spacing = 400,
  anchorKm
) {
  const bearing = pickSeaBearing(areaName, baseLon);
  const distKm = anchorKm ?? pickSeaDistanceKm(areaName, FIVE_MILES_KM);
  const anchor = moveByBearing(baseLat, baseLon, distKm * 1000, bearing);
  const lateral = (bearing + 90) % 360;
  const half = (count - 1) / 2;
  return Array.from({ length: count }, (_, i) => {
    const delta = (i - half) * spacing;
    const dir = delta >= 0 ? lateral : (lateral + 180) % 360;
    return moveByBearing(anchor.lat, anchor.lng, Math.abs(delta), dir);
  });
}
const toRad = (d) => (d * Math.PI) / 180,
  toDeg = (r) => (r * 180) / Math.PI;
function greatCircleRoute(start, end, segments = 160) {
  const [lat1, lon1] = [toRad(start.lat), toRad(start.lng)],
    [lat2, lon2] = [toRad(end.lat), toRad(end.lng)];
  const d =
    2 *
    Math.asin(
      Math.sqrt(
        Math.sin((lat2 - lat1) / 2) ** 2 +
          Math.cos(lat1) * Math.cos(lat2) * Math.sin((lon2 - lon1) / 2) ** 2
      )
    );
  if (!d) return [start, end];
  const out = [];
  for (let i = 0; i <= segments; i++) {
    const f = i / segments,
      A = Math.sin((1 - f) * d) / Math.sin(d),
      B = Math.sin(f * d) / Math.sin(d);
    const x =
      A * Math.cos(lat1) * Math.cos(lon1) + B * Math.cos(lat2) * Math.cos(lon2);
    const y =
      A * Math.cos(lat1) * Math.sin(lon1) + B * Math.cos(lat2) * Math.sin(lon2);
    const z = A * Math.sin(lat1) + B * Math.sin(lat2);
    const lat = Math.atan2(z, Math.sqrt(x * x + y * y));
    const lon = Math.atan2(y, x);
    out.push({ lat: toDeg(lat), lng: ((toDeg(lon) + 540) % 360) - 180 });
  }
  return out;
}

const ALWAYS_VISIBLE_PORTS = [];
const ROUTE_END_KM_OVERRIDES = { kandla: 4, "deendayal port": 4 };

/* ================= Local caches (area & port) ================= */
const AREA_GEO_CACHE_KEY = "areaGeoCache_v2_osm";
const GEO_CACHE_KEY = "portGeoCache_v2_osm";
const GEO_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const readAreaGeoCache = () => {
  try {
    return JSON.parse(localStorage.getItem(AREA_GEO_CACHE_KEY) || "{}");
  } catch {
    return {};
  }
};
const writeAreaGeoCache = (obj) => {
  try {
    localStorage.setItem(AREA_GEO_CACHE_KEY, JSON.stringify(obj));
  } catch {}
};
const readGeoCache = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(GEO_CACHE_KEY) || "{}");
    const now = Date.now();
    const cleaned = {};
    for (const k in raw) {
      const v = raw[k];
      if (!v) continue;
      if (!GEO_CACHE_TTL_MS || !v.t || now - v.t < GEO_CACHE_TTL_MS)
        cleaned[k] = v;
    }
    if (Object.keys(cleaned).length !== Object.keys(raw).length) {
      localStorage.setItem(GEO_CACHE_KEY, JSON.stringify(cleaned));
    }
    return cleaned;
  } catch {
    return {};
  }
};
const writeGeoCache = (obj) => {
  try {
    localStorage.setItem(GEO_CACHE_KEY, JSON.stringify(obj));
  } catch {}
};

/* ================= FREE batch geocoder (Photon/OSM) ================= */
const PHOTON_URL = "https://photon.komoot.io/api";
const reqQueue = [];
let busy = false;
function delay(ms) {
  return new Promise((r) => setTimeout(r, ms));
}
async function photonSearch(q) {
  const url = `${PHOTON_URL}?q=${encodeURIComponent(q)}&limit=1`;
  const r = await fetch(url, { headers: { Accept: "application/json" } });
  if (!r.ok) return null;
  const j = await r.json().catch(() => null);
  const f = j?.features?.[0];
  if (!f?.geometry?.coordinates) return null;
  const [lon, lat] = f.geometry.coordinates;
  if (typeof lat === "number" && typeof lon === "number") return { lat, lng: lon };
  return null;
}
function enqueueGeocode(task) {
  reqQueue.push(task);
  pumpQueue();
}
async function pumpQueue() {
  if (busy) return;
  const next = reqQueue.shift();
  if (!next) return;
  busy = true;
  try {
    const { q, resolve } = next;
    let pos = null;
    const hasPortWord = /\bport\b/i.test(q);
    if (!hasPortWord) pos = await photonSearch(`${q} port`);
    if (!pos) pos = await photonSearch(q);
    resolve(pos);
  } catch {
    next.resolve(null);
  } finally {
    await delay(250);
    busy = false;
    if (reqQueue.length) pumpQueue();
  }
}
function geocodeBatch(queries) {
  return Promise.all(
    queries.map(
      (q) =>
        new Promise((resolve) => {
          enqueueGeocode({ q, resolve });
        })
    )
  );
}

/* ================= lookup helpers ================= */
function distKm(a, b) {
  const R = 6371,
    dLat = ((b.lat - a.lat) * Math.PI) / 180,
    dLon = ((b.lng - a.lng) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180,
    la2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
function getShipAreaPos(ship, areaCoordsMap) {
  const areaKey = normAreaKey(ship?.open_area || "");
  const hit = areaCoordsMap[areaKey];
  if (hit) return { lat: hit[0], lng: hit[1] };
  const ov = applyAreaOverrideIfAny(areaKey, undefined);
  if (ov) return ov;
  return globalSeaFallback(areaKey);
}
function globalSeaFallback(areaName) {
  const k = normAreaKey(areaName || "");
  if (k.includes("us ") || k.includes("america")) return { lat: 27, lng: -70 };
  if (k.includes("europe") || k.includes("med")) return { lat: 35, lng: 15 };
  if (k.includes("asia") || k.includes("china") || k.includes("japan"))
    return { lat: 27, lng: 130 };
  return { lat: 10, lng: 60 };
}

/* ===== Cargo key/utils ===== */
const getCargoKey = (c) =>
  `${c?.id || c?._id || c?.reference_id || "noid"}::${norm(
    String(c?.loading_port || "")
  )}::${norm(String(c?.discharge_port || ""))}`;
const buildCargoShareUrl = (cargoKey) => {
  const { origin, pathname } = window.location;
  const url = new URL(origin + pathname);
  url.searchParams.set("cargo", cargoKey);
  return url.toString();
};
async function shareCargoOnWhatsApp(cargoKey, cargo) {
  const url = buildCargoShareUrl(cargoKey);
  const title = `${cargo?.type_of_cargo || "Cargo"} Available`;
  const text = `Check cargo from ${cargo?.loading_port || "-"} to ${
    cargo?.discharge_port || "-"
  }.`;
  try {
    if (navigator.share) {
      await navigator.share({ title, text, url });
      return;
    }
  } catch (_) {}
  const wa = `https://wa.me/?text=${encodeURIComponent(
    `${title}\n${text}\n${url}`
  )}`;
  window.open(wa, "_blank", "noopener,noreferrer");
}

/* ============================== Component ============================== */
export default function Map() {
  const [ships, setShips] = useState([]);
  const [cargos, setCargos] = useState([]);

  // loading states
  const [loadingShips, setLoadingShips] = useState(true);
  const [loadingCargos, setLoadingCargos] = useState(true);

  const [selectedCargoKey, setSelectedCargoKey] = useState(null);
  const [showCargos, setShowCargos] = useState(false);

  // selected ship (NO BLINK, only halo + bigger icon)
  const [selectedShipKey, setSelectedShipKey] = useState(null);
  const [selectedShipPos, setSelectedShipPos] = useState(null);

  // Edit/Delete/Repost
  const [editShip, setEditShip] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [repostingId, setRepostingId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [portCoords, setPortCoords] = useState(() => {
    const cache = readGeoCache();
    const map = {};
    for (const k in cache) {
      const v = cache[k];
      if (v?.lat != null && v?.lon != null)
        map[k] = { lat: +v.lat, lng: +v.lon };
    }
    return map;
  });
  const [areaCoords, setAreaCoords] = useState(() => {
    const cache = readAreaGeoCache();
    const map = {};
    for (const k in cache) {
      const aliasKey = normAreaKey(k);
      const v = cache[k];
      if (Array.isArray(v) && v.length === 2) {
        const ov = applyAreaOverrideIfAny(aliasKey, [+v[0], +v[1]]);
        map[aliasKey] = [ov.lat, ov.lng];
      }
    }
    return map;
  });

  const [hoveredShipKey, setHoveredShipKey] = useState(null);
  const [hoveredCargoKey, setHoveredCargoKey] = useState(null);

  const [activeMapType, setActiveMapType] = useState("roadmap");
  const [user, setUser] = useState(null);
  const [userPos, setUserPos] = useState(null);
  const [route, setRoute] = useState(null);
  const [dest, setDest] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapZoom, setMapZoom] = useState(5);

  const mapRef = useRef(null);
 const { isLoaded } = useJsApiLoader(GMAPS_LOADER_OPTIONS);

  const navigate = useNavigate();

  // DESKTOP left sidebar
  const [showSidebar, setShowSidebar] = useState(true);
  const [sidebarMode, setSidebarMode] = useState("recent");
  const [sidebarSearch, setSidebarSearch] = useState("");
  const [ownerOpenKey, setOwnerOpenKey] = useState(null);
  const listWrapRef = useRef(null);
  const cardRefs = useRef({});
  const [nearbyAnchor, setNearbyAnchor] = useState(null);

  // MOBILE bottom sheet
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileListRef = useRef(null);

  const EXPAND_MS = 300;

  // NEW: which ship keys are shown when a cluster is clicked
  const [clusterFilterKeys, setClusterFilterKeys] = useState(null);

  /* ===== Scroll helper: keep selected card visible without jumping to very top ===== */
  const SIDEBAR_TOP_OFFSET = 170;
  const SIDEBAR_BOTTOM_PAD = 16;

  const scrollEnsureFullyVisible = useCallback(
    (key, { preferTop = true } = {}) => {
      const wrap = listWrapRef.current;
      const el = cardRefs.current[key];
      if (!wrap || !el) return;

      const elTop = el.offsetTop;
      const elBottom = elTop + el.offsetHeight;
      const viewTop = wrap.scrollTop;
      const viewBottom = viewTop + wrap.clientHeight;

      const topLimit = viewTop + SIDEBAR_TOP_OFFSET;
      const bottomLimit = viewBottom - SIDEBAR_BOTTOM_PAD;

      let nextTop = null;

      // If card is above visible area (under the header) → scroll up just enough
      if (elTop < topLimit) {
        nextTop = Math.max(0, elTop - SIDEBAR_TOP_OFFSET);
      }
      // If card's bottom is below visible area → scroll down just enough
      else if (elBottom > bottomLimit) {
        nextTop = Math.max(
          0,
          elBottom - wrap.clientHeight + SIDEBAR_BOTTOM_PAD
        );
      }

      if (nextTop != null) {
        wrap.scrollTo({ top: nextTop, behavior: "smooth" });
      }
    },
    []
  );

  const scrollIntoViewIfClipped = (key, { preferTop = true } = {}) =>
    scrollEnsureFullyVisible(key, { preferTop });

  useEffect(() => {
    if (!selectedShipKey) return;
    setTimeout(
      () => scrollIntoViewIfClipped(selectedShipKey, { preferTop: false }),
      EXPAND_MS + 40
    );
  }, [selectedShipKey]); // eslint-disable-line

  useEffect(() => {
    setMapReady(true);
    const cached = readLastUserPos();
    if (cached && !userPos) setUserPos(cached);
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const fast = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        writeLastUserPos(fast);
        setUserPos((prev) => prev || fast);
      },
      () => {},
      {
        enableHighAccuracy: false,
        timeout: 3000,
        maximumAge: 6 * 60 * 60 * 1000,
      }
    );
  }, []);

  useEffect(() => {
    const cache = readAreaGeoCache();
    let changed = false;
    ["bombay", "bombay, india"].forEach((k) => {
      const nk = norm(k);
      if (cache[nk]) {
        delete cache[nk];
        changed = true;
      }
    });
    if (changed) writeAreaGeoCache(cache);
  }, []);

  const token = useMemo(
    () => localStorage.getItem("token") || sessionStorage.getItem("token"),
    []
  );
  const authHeaders = token
    ? { Authorization: `Token ${token}`, Accept: "application/json" }
    : { Accept: "application/json" };

  const [navVariant, setNavVariant] = useState(() => {
    const cachedAdmin = localStorage.getItem("is_superuser") === "1";
    if (cachedAdmin) return "admin";
    if (token) return "user";
    return "guest";
  });
  const isLoggedIn = !!token;
  const isAdmin = useMemo(
    () =>
      navVariant === "admin" ||
      !!user?.is_superuser ||
      localStorage.getItem("is_superuser") === "1",
    [navVariant, user]
  );

  useEffect(() => {
    if (!token) {
      setNavVariant("guest");
      return;
    }
    const controller = new AbortController();
    let ignore = false;
    (async () => {
      try {
        const res = await axios.get(
          `${config.base_url}/api/HappyMarineShipping/viewusers`,
          {
            headers: authHeaders,
            signal: controller.signal,
          }
        );
        if (ignore) return;
        if (res?.data?.status === 200) {
          const u = res.data.data;
          setUser(u);
          const isAdminLocal = !!u?.is_superuser;
          setNavVariant(isAdminLocal ? "admin" : "user");
          localStorage.setItem("is_superuser", isAdminLocal ? "1" : "0");
        } else {
          setNavVariant("guest");
          localStorage.removeItem("is_superuser");
        }
      } catch {
        if (ignore) return;
        setNavVariant("guest");
        localStorage.removeItem("is_superuser");
      }
    })();
    return () => {
      ignore = true;
      controller.abort();
    };
  }, [token]); // eslint-disable-line

  // Fetch ships
  useEffect(() => {
    (async () => {
      setLoadingShips(true);
      try {
        const r = await axios.get(
          `${config.base_url}/api/HappyMarineShipping/viewShipForCharter`,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        if (r.data?.status === 200) {
          const rows = (r.data.data || []).filter((s) =>
            isVisible(s?.is_status)
          );
          setShips(rows);
        } else {
          setShips([]);
        }
      } catch (e) {
        console.error("fetch ships failed", e);
        setShips([]);
      } finally {
        setLoadingShips(false);
      }
    })();
  }, []);

  // Fetch cargos
  useEffect(() => {
    (async () => {
      setLoadingCargos(true);
      try {
        const r = await axios.get(
          `${config.base_url}/api/HappyMarineShipping/viewLogistics`,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        if (r.data?.status === 200) {
          const rows = (r.data.data || []).filter((c) =>
            isVisible(c?.is_status)
          );
          setCargos(rows);
        } else {
          setCargos([]);
        }
      } catch (e) {
        console.error("fetch cargos failed", e);
        setCargos([]);
      } finally {
        setLoadingCargos(false);
      }
    })();
  }, []);

  // deep-links
  const deepCargoRef = useRef(
    (() => {
      try {
        return new URL(window.location.href).searchParams.get("cargo");
      } catch {
        return null;
      }
    })()
  );
  useEffect(() => {
    if (deepCargoRef.current) setShowCargos(true);
  }, []);
  const deepShipRef = useRef(
    (() => {
      try {
        return new URL(window.location.href).searchParams.get("ship");
      } catch {
        return null;
      }
    })()
  );
  useEffect(() => {
    if (deepShipRef.current) setSelectedShipKey(deepShipRef.current);
  }, []);

  /* ========== FREE geocode batch (Photon) ========== */
  const asPortKey = (s) => {
    const k = norm(String(s || ""));
    if (!k) return "";
    const SHIP_PORT_ALIASES = {
      "king abdulaziz port": "dammam port",
      dammam: "dammam port",
      "dammam port": "dammam port",
      "ras tanura": "ras tanura port",
      "ras tanura port": "ras tanura port",
      jubail: "jubail port",
      "jubail port": "jubail port",
      "jebel ali": "jebel ali port",
      "jabal ali": "jebel ali port",
      manama: "manama port",
      bahrain: "manama port",
    };
    const alias = SHIP_PORT_ALIASES[k] || k;
    return /\bport\b/.test(alias) ? alias : `${alias} port`;
  };

  const geocodeMany = useCallback(
    async (queries, cacheKey, writeCacheFn, mapSetter, normalizeKeyFn) => {
      if (!queries?.length) return;
      const now = Date.now();
      const cache =
        cacheKey === GEO_CACHE_KEY ? readGeoCache() : readAreaGeoCache();

      const wanted = [];
      const results = {};
      const seen = new Set();

      // Dedup & check cache
      for (const raw of queries) {
        const key = normalizeKeyFn ? normalizeKeyFn(raw) : norm(raw);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        const hit = cache[key];
        if (hit?.lat != null && hit?.lon != null) {
          results[key] = { lat: +hit.lat, lng: +hit.lon };
        } else {
          wanted.push({ raw, key });
        }
      }
      if (!wanted.length) {
        mapSetter((prev) => {
          const merged = { ...prev };
          Object.entries(results).forEach(([k, p]) => {
            merged[k] =
              cacheKey === GEO_CACHE_KEY
                ? { lat: p.lat, lng: p.lng }
                : [p.lat, p.lng];
          });
          return merged;
        });
        return;
      }

      // Run batch (Photon)
      const batchQueries = wanted.map(({ raw }) => raw);
      const batchPos = await geocodeBatch(batchQueries);

      for (let i = 0; i < wanted.length; i++) {
        const { key } = wanted[i];
        const pos = batchPos[i];
        if (pos) {
          results[key] = pos;
          cache[key] = { lat: pos.lat, lon: pos.lng, t: now };
        }
      }
      writeCacheFn(cache);

      mapSetter((prev) => {
        const merged = { ...prev };
        Object.entries(results).forEach(([k, p]) => {
          merged[k] =
            cacheKey === GEO_CACHE_KEY
              ? { lat: p.lat, lng: p.lng }
              : [p.lat, p.lng];
        });
        return merged;
      });
    },
    []
  );

  // Geocode cargo ports
  useEffect(() => {
    (async () => {
      const all = [];
      (cargos || []).forEach((c) => {
        if (c?.loading_port) all.push(norm(String(c.loading_port)));
        if (c?.discharge_port) all.push(norm(String(c.discharge_port)));
      });
      const unique = Array.from(new Set(all)).filter(Boolean);
      if (!unique.length) return;
      await geocodeMany(
        unique,
        GEO_CACHE_KEY,
        writeGeoCache,
        setPortCoords,
        (s) => norm(String(s || ""))
      );
    })();
  }, [cargos, geocodeMany]);

  // how many of those actually have coordinates on the map
  const shipsOnMapCount = useMemo(() => {
    return ships.filter((s) => {
      if (!isVisible(s?.is_status)) return false;

      const areaKey = normAreaKey(s?.open_area || "");
      const portKey = asPortKey(s?.open_area);

      const portPos = portCoords[portKey];
      const areaPos = areaCoords[areaKey];

      return !!(portPos || areaPos);
    }).length;
  }, [ships, portCoords, areaCoords]);

  // Geocode ship open_area AS PORT
  useEffect(() => {
    if (!ships?.length) return;
    const uniquePortQueries = Array.from(
      new Set(ships.map((s) => asPortKey(s?.open_area)).filter(Boolean))
    );
    if (!uniquePortQueries.length) return;
    geocodeMany(
      uniquePortQueries,
      GEO_CACHE_KEY,
      writeGeoCache,
      setPortCoords,
      (s) => asPortKey(s)
    );
  }, [ships, geocodeMany]);

  // Geocode general areas (fallbacks) — for nearby sorting
  useEffect(() => {
    (async () => {
      const unique = Array.from(
        new Set(
          ships
            .map((s) => s?.open_area)
            .filter(Boolean)
            .map((x) => normAreaKey(x))
        )
      );
      if (!unique.length) return;
      await geocodeMany(
        unique,
        AREA_GEO_CACHE_KEY,
        writeAreaGeoCache,
        setAreaCoords,
        (s) => normAreaKey(s)
      );
    })();
  }, [ships, geocodeMany]);

  // total visible ships (for “Showing X of Y ships”)
  const totalShipCount = useMemo(
    () => ships.filter((s) => isVisible(s?.is_status)).length,
    [ships]
  );

  // percentage for loading status
  const shipsLoadedPercentage = useMemo(() => {
    if (loadingShips) {
      if (!totalShipCount) return 0;
      const pct = Math.round((shipsOnMapCount / totalShipCount) * 100);
      return Math.min(pct, 99);
    }
    return 100;
  }, [loadingShips, shipsOnMapCount, totalShipCount]);

  /* ===== Icons (regular + selected sizes) ===== */
  const cargoIcon = useMemo(
    () =>
      window.google?.maps
        ? makeIconHD(CargoIcon, 52, 52, 26, 46)
        : { url: CargoIcon },
    [isLoaded]
  );
  const shipIcon = useMemo(
    () =>
      window.google?.maps
        ? makeIconHD(ShipIconImg, 52, 52, 26, 44)
        : { url: ShipIconImg },
    [isLoaded]
  );
  const selectedShipIconLarge = useMemo(
    () =>
      window.google?.maps
        ? makeIconHD(ShipIconImg, 60, 60, 30, 50)
        : { url: ShipIconImg },
    [isLoaded]
  );
  const selectedHaloIcon = useMemo(() => {
    if (!window.google?.maps) return undefined;
    return (scale, strokeOpacity = 0.9) => ({
      path: window.google.maps.SymbolPath.CIRCLE,
      scale,
      strokeColor: "#22c55e",
      strokeOpacity,
      strokeWeight: 3,
      fillColor: "#22c55e", // green fill
      fillOpacity: 0.28, // semi-transparent inside
    });
  }, [isLoaded]);

  const selectedCargoIcon = useMemo(
    () =>
      window.google?.maps
        ? makeIconHD(CargoIcon, 58, 58, 29, 50)
        : { url: CargoIcon },
    [isLoaded]
  );

  useEffect(() => {
    [CargoIcon, ShipIconImg].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (selectedShipKey) url.searchParams.set("ship", selectedShipKey);
    else url.searchParams.delete("ship");
    window.history.replaceState({}, "", url.toString());
  }, [selectedShipKey]);
  useEffect(() => {
    const url = new URL(window.location.href);
    if (selectedCargoKey) url.searchParams.set("cargo", selectedCargoKey);
    else url.searchParams.delete("cargo");
    window.history.replaceState({}, "", url.toString());
  }, [selectedCargoKey]);

  const handleCargoClick = useCallback(
    async (cargo, start, end) => {
      const cargoKey = getCargoKey(cargo);
      if (selectedCargoKey === cargoKey) {
        setSelectedCargoKey(null);
        setRoute(null);
        setDest(null);
        return;
      }
      setSelectedCargoKey(cargoKey);
      try {
        const resp = await axios.post(
          `${config.base_url}/api/HappyMarineShipping/sea-route`,
          {
            start: { lat: start.lat, lon: start.lng },
            end: { lat: end.lat, lon: end.lng },
          }
        );
        let coords = resp.data?.coordinates;
        if (!Array.isArray(coords) || coords.length < 2) {
          coords = greatCircleRoute(start, end, 160);
        } else {
          coords = coords.map(([lat, lon]) => ({ lat, lng: lon }));
        }
        setRoute(coords);
        setDest(end);
        if (mapRef.current && window.google && coords.length >= 2) {
          const b = new window.google.maps.LatLngBounds();
          coords.forEach((p) => b.extend(p));
          mapRef.current.fitBounds(b, 60);
        }
      } catch {
        const coords = greatCircleRoute(start, end, 160);
        setRoute(coords);
        setDest(end);
        if (mapRef.current && window.google && coords.length >= 2) {
          const b = new window.google.maps.LatLngBounds();
          coords.forEach((p) => b.extend(p));
          mapRef.current.fitBounds(b, 60);
        }
      }
    },
    [selectedCargoKey]
  );

  const clearSelection = useCallback(
    () => {
      let changed = false;
      if (selectedShipKey) {
        setSelectedShipKey(null);
        setSelectedShipPos(null);
        changed = true;
      }
      if (selectedCargoKey) {
        setSelectedCargoKey(null);
        changed = true;
      }
      if (route || dest) {
        setRoute(null);
        setDest(null);
        changed = true;
      }
      if (clusterFilterKeys) {
        setClusterFilterKeys(null);
        changed = true;
      }
      // when everything is cleared, go back to "recent" mode
      setSidebarMode("recent");
      return changed;
    },
    [selectedShipKey, selectedCargoKey, route, dest, clusterFilterKeys]
  );

  const selectedCargo = useMemo(
    () =>
      selectedCargoKey
        ? cargos.find((c) => getCargoKey(c) === selectedCargoKey) || null
        : null,
    [selectedCargoKey, cargos]
  );
  useEffect(() => {
    if (!selectedShipKey) return;
    const s = ships.find((x) => getShipKey(x) === selectedShipKey);
    const base = s ? getShipAreaPos(s, areaCoords) : null;
    if (base) setNearbyAnchor({ key: selectedShipKey, pos: base });
  }, [selectedShipKey, ships, areaCoords]);

  // CLUSTER WHEN ZOOMED OUT, SIDE-BY-SIDE WHEN ZOOMED IN
  const HIGH_ZOOM_FOR_SIDE_BY_SIDE = 9;
  const spacingByZoom = mapZoom >= 12 ? 600 : mapZoom >= 10 ? 500 : 380;

  /* ==================== SHIP MARKERS WITH CLUSTERING ==================== */
  const shipMarkers = useMemo(() => {
    const visibleShips = ships.filter((s) => isVisible(s?.is_status));
    if (!visibleShips.length) return null;

    // Group ships by normalized open_area (areaKey)
    const groupsMap = {};
    visibleShips.forEach((ship) => {
      const areaKey = normAreaKey(ship?.open_area || "");
      if (!areaKey) return;

      const portKey = asPortKey(ship?.open_area);
      const portPos = portCoords[portKey];
      let basePos;

      if (portPos && portPos.lat != null && portPos.lng != null) {
        basePos = { lat: +portPos.lat, lng: +portPos.lng };
      } else {
        basePos = getShipAreaPos(ship, areaCoords);
      }
      if (!basePos) return;

      if (!groupsMap[areaKey]) {
        groupsMap[areaKey] = {
          areaKey,
          pos: basePos,
          ships: [],
        };
      }
      groupsMap[areaKey].ships.push(ship);
    });

    const groups = Object.values(groupsMap);
    const markers = [];

    // 1) CLUSTER MODE (zoomed out)
    if (mapZoom < HIGH_ZOOM_FOR_SIDE_BY_SIDE) {
      groups.forEach((group) => {
        const { areaKey, pos, ships: groupShips } = group;
        if (!pos || !groupShips.length) return;
        const count = groupShips.length;

        // If only ONE ship in this port/area → show normal single marker (no cluster)
        if (count === 1) {
          const ship = groupShips[0];
          const shipKeyFull = getShipKey(ship);
          const isSelected = shipKeyFull === selectedShipKey;
          const isHovered = hoveredShipKey === shipKeyFull;
          const candidate = pos;

          const onClick = (e) => {
            e?.domEvent?.stopPropagation?.();
            setShowSidebar(true);
            setClusterFilterKeys(null); // clear any cluster filter

            setSelectedShipKey((k) => {
              const next = k === shipKeyFull ? null : shipKeyFull;
              if (next) {
                setSelectedShipPos(candidate);
                const basePos = getShipAreaPos(ship, areaCoords);
                if (basePos) setNearbyAnchor({ key: shipKeyFull, pos: basePos });

                if (mapRef.current && window.google) {
                  const z = Math.max(mapRef.current.getZoom() || 5, 7);
                  mapRef.current.panTo(candidate);
                  mapRef.current.setZoom(z);
                }

                // auto-switch to NEARBY when selecting a single ship
                setSidebarMode("nearby");
              } else {
                setSelectedShipPos(null);
                setSidebarMode("recent");
              }
              return next;
            });
          };

          const showBadge = shouldShowShipLabel({
            isSelected,
            isHovered,
            mapZoom,
            groupCount: 1,
            indexInGroup: 0,
          });

          markers.push(
            <React.Fragment key={`ship-single-${shipKeyFull}`}>
              {isSelected && selectedHaloIcon && (
                <MarkerF
                  position={candidate}
                  icon={selectedHaloIcon(27, 0.7)}
                  zIndex={690}
                  clickable={false}
                />
              )}
              <MarkerF
                position={candidate}
                icon={isSelected ? selectedShipIconLarge : shipIcon}
                onClick={onClick}
                onMouseOver={() => setHoveredShipKey(shipKeyFull)}
                onMouseOut={() =>
                  setHoveredShipKey((k) => (k === shipKeyFull ? null : k))
                }
                options={{ optimized: false }}
                zIndex={isSelected ? 800 : 700}
              />
              {showBadge && mapZoom >= REF_BADGE_MIN_ZOOM && (
                <MarkerF
                  position={candidate}
                  icon={makeRefBadgeIcon(shipRefText(ship))}
                  clickable={false}
                  zIndex={isSelected ? 850 : 730}
                />
              )}
            </React.Fragment>
          );
          return;
        }

        // --------- CLUSTER (count > 1) ---------
        const sortedShips = [...groupShips].sort(
          (a, b) => getShipTimestamp(b) - getShipTimestamp(a)
        );
        const latestShip = sortedShips[0];
        const latestKey = latestShip ? getShipKey(latestShip) : null;
        const clusterKeys = sortedShips.map((s) => getShipKey(s));

        const onClusterClick = (e) => {
          e?.domEvent?.stopPropagation?.();

          // Zoom in a bit
          if (mapRef.current && window.google) {
            const current = mapRef.current.getZoom() || 5;
            const targetZoom = Math.max(
              HIGH_ZOOM_FOR_SIDE_BY_SIDE,
              current + 1
            );
            mapRef.current.panTo(pos);
            mapRef.current.setZoom(targetZoom);
          }

          if (latestShip && latestKey) {
            setShowSidebar(true);
            setMobileOpen(true);
            // cluster click → keep RECENT mode, but filter to this cluster
            setSidebarMode("recent");
            setClusterFilterKeys(clusterKeys);

            setSelectedShipKey(latestKey);
            setSelectedShipPos(pos);

            const basePos = getShipAreaPos(latestShip, areaCoords);
            if (basePos) setNearbyAnchor({ key: latestKey, pos: basePos });

            setTimeout(
              () =>
                scrollEnsureFullyVisible(latestKey, {
                  preferTop: false,
                }),
              EXPAND_MS + 40
            );
          }
        };

        markers.push(
          <React.Fragment key={`ship-cluster-${areaKey}`}>
            {/* Base cluster icon (ship pin) */}
            <MarkerF
              position={pos}
              icon={shipIcon}
              onClick={onClusterClick}
              zIndex={750}
            />
            {/* Round count badge on top-right of pin */}
            <MarkerF
              position={pos}
              icon={makeClusterCountIcon(count)}
              clickable={false}
              zIndex={760}
            />
          </React.Fragment>
        );
      });
      return markers;
    }

    // 2) SIDE-BY-SIDE MODE (zoomed in)
    groups.forEach((group) => {
      const { areaKey, pos, ships: groupShips } = group;
      if (!pos || !groupShips.length) return;

      const positions = sideBySidePositions(
        pos.lat,
        pos.lng,
        areaKey,
        groupShips.length,
        spacingByZoom
      );

      groupShips.forEach((ship, index) => {
        const shipKeyFull = getShipKey(ship);
        const isSelected = shipKeyFull === selectedShipKey;
        const isHovered = hoveredShipKey === shipKeyFull;
        const candidate = positions[index];

        const onClick = (e) => {
          e?.domEvent?.stopPropagation?.();
          // user clicked a specific ship pin (not cluster) → clear cluster filter and show nearby
          setClusterFilterKeys(null);

          setSelectedShipKey((k) => {
            const next = k === shipKeyFull ? null : shipKeyFull;
            if (next) {
              setSelectedShipPos(candidate);
              const basePos = getShipAreaPos(ship, areaCoords);
              if (basePos) setNearbyAnchor({ key: shipKeyFull, pos: basePos });

              if (mapRef.current && window.google) {
                const z = Math.max(mapRef.current.getZoom() || 5, 7);
                mapRef.current.panTo(candidate);
                mapRef.current.setZoom(z);
              }

              setSidebarMode("nearby");
            } else {
              setSelectedShipPos(null);
              setSidebarMode("recent");
            }
            return next;
          });
        };

        const showBadge = shouldShowShipLabel({
          isSelected,
          isHovered,
          mapZoom,
          groupCount: groupShips.length,
          indexInGroup: index,
        });

        const mainMarker = (
          <MarkerF
            key={`ship-${shipKeyFull}`}
            position={candidate}
            icon={isSelected ? selectedShipIconLarge : shipIcon}
            onClick={onClick}
            onMouseOver={() => setHoveredShipKey(shipKeyFull)}
            onMouseOut={() =>
              setHoveredShipKey((k) => (k === shipKeyFull ? null : k))
            }
            options={{ optimized: false }}
            zIndex={isSelected ? 800 : 700}
          />
        );

        const badgeMarker =
          showBadge && mapZoom >= REF_BADGE_MIN_ZOOM ? (
            <MarkerF
              key={`ship-badge-${shipKeyFull}`}
              position={candidate}
              icon={makeRefBadgeIcon(shipRefText(ship))}
              clickable={false}
              zIndex={isSelected ? 850 : 730}
            />
          ) : null;

        markers.push(
          <React.Fragment key={`ship-wrap-${shipKeyFull}`}>
            {isSelected && selectedHaloIcon && (
              <MarkerF
                position={candidate}
                icon={selectedHaloIcon(16, 0.9)} // NO BLINK, fixed halo
                zIndex={690}
                clickable={false}
              />
            )}
            {mainMarker}
            {badgeMarker}
          </React.Fragment>
        );
      });
    });

    return markers;
  }, [
    ships,
    portCoords,
    areaCoords,
    mapZoom,
    shipIcon,
    selectedShipIconLarge,
    selectedShipKey,
    hoveredShipKey,
    selectedHaloIcon,
    spacingByZoom,
    scrollEnsureFullyVisible,
  ]);

  /* ==================== CARGO MARKERS ==================== */
  const cargoMarkers = useMemo(() => {
    const countsByLoadPort = {};
    cargos
      .filter((c) => isVisible(c?.is_status))
      .forEach((c) => {
        const loadKey = norm(String(c?.loading_port || ""));
        countsByLoadPort[loadKey] = (countsByLoadPort[loadKey] || 0) + 1;
      });

    const indexByLoadPort = {};

    return cargos
      .filter((c) => isVisible(c?.is_status))
      .map((cargo) => {
        const loadKey = norm(String(cargo.loading_port || ""));
        const discKey = norm(String(cargo.discharge_port || ""));
        const start = portCoords[loadKey];
        const end = portCoords[discKey];
        if (!start) return null;

        const cargoKey = getCargoKey(cargo);
        const isSelected = cargoKey === selectedCargoKey;
        const isHovered = hoveredCargoKey === cargoKey;

        const groupCount = countsByLoadPort[loadKey] || 1;
        const idx = indexByLoadPort[loadKey] || 0;
        indexByLoadPort[loadKey] = idx + 1;

        const showLabel = shouldShowCargoLabel({
          isSelected,
          isHovered,
          mapZoom,
          groupCount,
          indexInGroup: idx,
        });

        const onClick = (e) => {
          e?.domEvent?.stopPropagation?.();
          if (start && end) handleCargoClick(cargo, start, end);
        };

        return (
          <React.Fragment key={`cargo-${cargoKey}`}>
            {showLabel && (
              <MarkerF
                position={start}
                icon={makeRefBadgeIcon(cargoRefText(cargo), { yOffset: 22 })}
                clickable={false}
                zIndex={isSelected ? 910 : 660}
                options={{ optimized: false }}
                onMouseOver={() => setHoveredCargoKey(cargoKey)}
                onMouseOut={() =>
                  setHoveredCargoKey((k) => (k === cargoKey ? null : k))
                }
              />
            )}

            <MarkerF
              position={start}
              icon={isSelected ? selectedCargoIcon : cargoIcon}
              onClick={onClick}
              onMouseOver={() => setHoveredCargoKey(cargoKey)}
              onMouseOut={() =>
                setHoveredCargoKey((k) => (k === cargoKey ? null : k))
              }
              options={{ optimized: false }}
              zIndex={isSelected ? 900 : 650}
            />
          </React.Fragment>
        );
      })
      .filter(Boolean);
  }, [
    cargos,
    portCoords,
    selectedCargoKey,
    handleCargoClick,
    cargoIcon,
    selectedCargoIcon,
    mapZoom,
    hoveredCargoKey,
  ]);

  const handleViewList = () => {
    if (user?.is_superuser) navigate("/adminChartering");
    else if (user) navigate("/usershipreadytochart");
    else navigate("/shipreadytochart");
  };

  // Edit / Save / Repost / Delete
  const refUrlFromShip = (s) =>
    s?.ref_email_url ||
    s?.reference_email_url ||
    s?.email_source_url ||
    s?.email_url ||
    s?.ref_url ||
    s?.url ||
    "";

  const openEdit = (ship) => {
    if (!isLoggedIn) {
      alert("Please log in to edit.");
      return;
    }
    setEditShip({
      ...ship,
      __refUrl: refUrlFromShip(ship),
    });
  };

  const handleSaveEdit = async () => {
    if (!editShip) return;
    const id = editShip?.id ?? editShip?._id;
    if (!id) {
      alert("Missing ship id.");
      return;
    }
    setSavingEdit(true);
    try {
      const payload = {
        vessel_type: editShip.vessel_type ?? "",
        open_area: editShip.open_area ?? "",
        DWT: editShip.DWT ?? "",
        year_built: editShip.year_built ?? "",
        tc_vc: editShip.tc_vc ?? "",
        lychan: editShip.lychan ?? "",
        brief_description: editShip.brief_description ?? "",
        name: editShip.name ?? editShip.owner_name ?? "",
        email: editShip.email ?? "",
        phone: editShip.phone ?? "",
        company: editShip.company ?? "",
        ship_code: editShip.ship_code ?? "",
      };

      await axios.put(
        `${config.base_url}/api/HappyMarineShipping/updateCharter/${id}`,
        payload,
        { headers: { "Content-Type": "multipart/form-data", ...authHeaders } }
      );

      setShips((prev) =>
        prev.map((s) =>
          String(s.id ?? s._id) === String(id) ? { ...s, ...payload } : s
        )
      );

      setEditShip(null);
    } catch (e) {
      console.error(e);
      alert("Update failed.");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleRepost = async (ship) => {
    const id = ship?.id ?? ship?._id;
    if (!id) return;
    if (!isLoggedIn) {
      alert("Please log in to repost.");
      return;
    }
    setRepostingId(id);
    try {
      const now = new Date().toISOString();
      await axios.put(
        `${config.base_url}/api/HappyMarineShipping/updateCharter/${id}`,
        { created_at: now, updated_at: now },
        { headers: { "Content-Type": "application/json", ...authHeaders } }
      );

      setShips((prev) =>
        prev
          .map((s) =>
            String(s.id ?? s._id) === String(id)
              ? { ...s, created_at: now, updated_at: now }
              : s
          )
          .sort((a, b) => getShipTimestamp(b) - getShipTimestamp(a))
      );
    } catch (e) {
      console.error(e);
      alert("Repost failed.");
    } finally {
      setRepostingId(null);
    }
  };

  const getId = (c) => c?.id ?? c?._id ?? c?.reference_id;

  async function confirmDeleteNow() {
    if (!confirmDel) return;
    const id = getId(confirmDel);
    if (!id) {
      alert("Missing ship id");
      return;
    }

    try {
      setIsDeleting(true);
      await axios.delete(
        `${config.base_url}/api/HappyMarineShipping/deleteCharter/${id}`,
        { headers: { ...(token ? { Authorization: `Token ${token}` } : {}) } }
      );

      setShips((prev) => prev.filter((x) => getId(x) !== id));
      toast.success("Ship listing deleted.");
      const delKey = getShipKey(confirmDel);
      if (selectedShipKey === delKey) setSelectedShipKey(null);
      setConfirmDel(null);
    } catch (e) {
      console.error(e);
      alert("Delete failed");
    } finally {
      setIsDeleting(false);
    }
  }
  function handleDelete(c) {
    setConfirmDel(c);
  }

  const mapOptions = useMemo(
    () => ({
      mapTypeId: activeMapType,
      disableDefaultUI: true,
      clickableIcons: false,
      keyboardShortcuts: false,
      gestureHandling: "greedy",
      minZoom: 2,
      maxZoom: 19,
      streetViewControl: false,
      fullscreenControl: true,
      styles:
        activeMapType === "roadmap"
          ? [
              { featureType: "poi", stylers: [{ visibility: "off" }] },
              { featureType: "transit", stylers: [{ visibility: "off" }] },
            ]
          : [],
    }),
    [activeMapType]
  );

  if (!isLoaded || !mapReady)
    return (
      <div className="w-full h-screen flex items-center justify-center text-slate-600">
        Loading map…
      </div>
    );

  /* ---------- helpers to build list items ---------- */
  function buildShipItemsForSidebar(mode, needle) {
    let items = [];
    if (mode === "nearby" && nearbyAnchor?.pos) {
      items = ships
        .filter((s) => isVisible(s?.is_status))
        .map((s) => {
          const key = getShipKey(s);
          const pos = getShipAreaPos(s, areaCoords);
          if (!pos) return null;
          return { ship: s, key, km: distKm(nearbyAnchor.pos, pos) };
        })
        .filter(Boolean)
        .sort((a, b) => a.km - b.km);
    } else {
      const recent = [...ships]
        .filter((s) => isVisible(s?.is_status))
        .sort((a, b) => getShipTimestamp(b) - getShipTimestamp(a));
      items = recent.map((s) => ({ ship: s, key: getShipKey(s), km: null }));
    }
    if (needle) {
      items = items.filter(({ ship: s }) =>
        [s?.open_area, s?.vessel_type, s?.DWT, s?.brief_description]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(needle))
      );
    }

    // if a cluster is active, show only its ships
    if (clusterFilterKeys && clusterFilterKeys.length) {
      items = items.filter(({ key }) => clusterFilterKeys.includes(key));
    }

    return items;
  }

  const desktopItems = buildShipItemsForSidebar(
    sidebarMode,
    sidebarSearch.trim().toLowerCase()
  );
  const mobileItems = desktopItems;
  const visibleShipCount = desktopItems.length;

  return (
    <div className="w-full overflow-hidden relative h-screen">
      {navVariant === "admin" ? (
        <NewAdminNav />
      ) : navVariant === "user" ? (
        <Usernav />
      ) : (
        <NewHomeNav/>
      )}

      {/* Floating opener button (desktop only) */}
      {!showSidebar && (
        <button
          className="xm:hidden fixed left-2 top-[180px] z-[1060] bg-[#253356] text-white px-3 py-2 rounded-lg shadow-2xl hover:bg-[#1b2a4f] transition-colors"
          onClick={() => setShowSidebar(true)}
          title="Open sidebar"
          aria-label="Open sidebar"
        >
          ☰ Ships Details
        </button>
      )}

      {!mobileOpen && (
        <button
          className="hidden xm:block fixed left-2 bottom-2 z-[1060] bg-[#253356] text-white px-3 py-2 rounded-lg shadow-2xl hover:bg-[#1b2a4f] transition-colors"
          onClick={() => setMobileOpen(true)}
          title="Open sidebar"
          aria-label="Open sidebar"
        >
          ^ Ships Details
        </button>
      )}

      {/* =================== DESKTOP LEFT SIDEBAR =================== */}
      <div
        className={`xm:hidden fixed top-[76px] left-0 z-[1050] transition-transform duration-300 ${
          showSidebar ? "translate-x-0" : "-translate-x-[320px]"
        }`}
        style={{ width: 320, maxWidth: "80vw", height: "100%" }}
      >
        <div className="h-full pb-28  mt-[36px] backdrop-blur-md bg-white/90 dark:bg-slate-900/85 border-r border-white/40 shadow-2xl">
          <div className="flex items-center justify-between px-3 py-2 border-t border-[#123557] bg-[#9ccee2] text-[#123557]">
            <div className="font-semibold text-sm">
              {sidebarMode === "nearby" ? "Nearby Ships" : "Recently Listed Ships"}
            </div>
            <button
              className="px-2 py-1 rounded bg-white/15 hover:bg-white/25"
              onClick={() => setShowSidebar((v) => !v)}
              title="Collapse"
            >
              {showSidebar ? "⟨" : "⟩"}
            </button>
          </div>
          <div className="p-3">
            <input
              value={sidebarSearch}
              onChange={(e) => setSidebarSearch(e.target.value)}
              placeholder="Search: area, type, DWT…"
              className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="px-3 pb-1 text-[11px] text-slate-500">
            {loadingShips
              ? "Loading ships…"
              : `Showing ${visibleShipCount} of ${totalShipCount} ships`}
          </div>
          {/* Title row only (Nearby button removed) */}
          <div className="flex items-center px-2 py-2 sticky top-[92px] z-10 bg-white/90 dark:bg-slate-900/85 backdrop-blur-md border-b border-white/30">
            <div className="text-xs font-semibold">
              {sidebarMode === "nearby" ? "Nearby Ships" : "Recently Listed Ships"}
            </div>
          </div>
          <div
            ref={listWrapRef}
            className="px-2 pb-3 overflow-y-auto custom-scroll"
            style={{ height: "calc(100% - 92px)" }}
          >
            {loadingShips ? (
              <div className="text-xs text-slate-500 px-2 py-2">
                Loading ships…
              </div>
            ) : desktopItems.length === 0 ? (
              <div className="text-xs text-slate-500 px-2">No ships found.</div>
            ) : (
              desktopItems.map(({ ship: s, key }) => {
                const selected = key === selectedShipKey;
                const ownerOpen = ownerOpenKey === key;
                const idStr = String(s?.id ?? s?._id ?? "");
                const isReposting =
                  repostingId && String(repostingId) === idStr;

                return (
                  <button
                    key={`side-${key}`}
                    ref={(el) => (cardRefs.current[key] = el)}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedShipKey((k) => {
                        const next = k === key ? null : key;
                        if (next) {
                          const base = getShipAreaPos(s, areaCoords);
                          if (base && mapRef.current && window.google) {
                            const z = Math.max(
                              mapRef.current.getZoom() || 5,
                              6
                            );
                            mapRef.current.panTo(base);
                            mapRef.current.setZoom(z);
                          }
                          // user explicitly chose one ship from list → clear cluster filter & show nearby
                          setClusterFilterKeys(null);
                          setSidebarMode("nearby");

                          setTimeout(
                            () =>
                              scrollEnsureFullyVisible(next, {
                                preferTop: false,
                              }),
                            EXPAND_MS + 40
                          );
                        } else {
                          setSidebarMode("recent");
                        }
                        return next;
                      });
                    }}
                    className={`w-full text-left mb-2 p-2 rounded-xl border transition ${
                      selected
                        ? "bg-gray-200"
                        : "bg-gray-50 shadow-lg dark:bg-slate-800 text-slate-800 border-2 border-slate-300 dark:text-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/70"
                    }`}
                  >
                    <div className="">
                      <div className="text-[13px] font-bold p-2 rounded-t bg-[#d1a460] text-black">
                        SHIP : {s?.vessel_type?.toUpperCase() || "Ship"} SHIP
                        FOR CHARTER
                      </div>
                    </div>
                    <ul className="list-disc p-2 list-inside space-y-1">
                      <li
                        className={`text-[11px] ${
                          selected ? "opacity-95" : "opacity-80"
                        }`}
                      >
                        <span className="font-semibold">Open Area</span> :{" "}
                        {s?.open_area || "-"}
                      </li>
                      <li
                        className={`text-[11px] ${
                          selected ? "opacity-95" : "opacity-80"
                        }`}
                      >
                        <span className="font-semibold">DWT</span> :{" "}
                        {s?.DWT || "-"}
                      </li>
                      <li
                        className={`text-[11px] ${
                          selected ? "opacity-95" : "opacity-80"
                        }`}
                      >
                        <span className="font-semibold">Built</span> :{" "}
                        {s?.year_built || "-"}
                      </li>
                      <li
                        className={`text-[15px] float-end list-none font-bold ${
                          selected ? "hidden" : ""
                        }`}
                      >
                        {s?.ship_code || "-"} - {s?.id}
                      </li>

                      <div
                        className="overflow-y-auto transition-[max-height] duration-300 ease-out"
                        style={{ maxHeight: selected ? 280 : 0 }}
                      >
                        <div className="pb-2">
                          <div className="text-[11px]">
                            <li>
                              <span className="font-semibold">TC/VC</span>:{" "}
                              {s?.tc_vc || "-"}
                            </li>
                            <li>
                              <span className="font-semibold">Laychan</span>:{" "}
                              {s?.lychan || "-"}
                            </li>
                            <div className="col-span-2">
                              {s?.brief_description
                                ?.split("\n")
                                .map((line, idx) => (
                                  <li
                                    key={idx}
                                    className="text-sm text-gray-800 leading-relaxed"
                                  >
                                    {line}
                                  </li>
                                ))}
                            </div>
                          </div>

                          {/* Action Row (Owner + Admin actions) */}
                          <div className="mt-2 flex items-center justify-between text-[9px] opacity-90">
                            <div className="w-full">
                              <button
                                className={`px-2 py-[6px] rounded text-white flex items-center gap-1 ${
                                  isLoggedIn
                                    ? "bg-blue-600 hover:bg-blue-700"
                                    : "bg-slate-400"
                                }`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!isLoggedIn) {
                                    alert(
                                      "Please log in to view owner details."
                                    );
                                    return;
                                  }
                                  setOwnerOpenKey((k) =>
                                    k === key ? null : key
                                  );
                                }}
                                aria-expanded={isLoggedIn ? ownerOpen : false}
                                aria-disabled={!isLoggedIn}
                                title={
                                  isLoggedIn
                                    ? "Show owner details"
                                    : "Login required"
                                }
                              >
                                Owner Information
                                <svg
                                  className={`h-3 w-3 transition-transform ${
                                    ownerOpen && isLoggedIn
                                      ? "rotate-180"
                                      : ""
                                  }`}
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </button>

                              <div
                                className="overflow-hidden transition-[max-height] duration-300 ease-out"
                                style={{
                                  maxHeight:
                                    isLoggedIn && ownerOpen ? 160 : 0,
                                }}
                              >
                                <div className="mt-2 rounded-lg bg-white/70 dark:bg-slate-700/40 border border-slate-200 dark:border-slate-600 p-2 text-[11px]">
                                  <ul className="space-y-1">
                                    <li>
                                      <span className="font-semibold">
                                        Name:
                                      </span>{" "}
                                      {s?.name || s?.owner_name || "-"}
                                    </li>
                                    <li>
                                      <span className="font-semibold">
                                        Email:
                                      </span>{" "}
                                      {s?.email ? (
                                        <a
                                          href={`mailto:${s.email}`}
                                          className="underline hover:no-underline"
                                          onClick={(e) =>
                                            e.stopPropagation()
                                          }
                                        >
                                          {s.email}
                                        </a>
                                      ) : (
                                        "-"
                                      )}
                                    </li>
                                    <li>
                                      <span className="font-semibold">
                                        Phone:
                                      </span>{" "}
                                      {s?.phone ? (
                                        <a
                                          href={`tel:${s.phone}`}
                                          className="underline hover:no-underline"
                                          onClick={(e) =>
                                            e.stopPropagation()
                                          }
                                        >
                                          {s.phone}
                                        </a>
                                      ) : (
                                        "-"
                                      )}
                                    </li>
                                    {s?.company && (
                                      <li>
                                        <span className="font-semibold">
                                          Company:
                                        </span>{" "}
                                        {s.company}
                                      </li>
                                    )}
                                  </ul>
                                </div>
                              </div>
                            </div>

                            {/* Admin Buttons */}
                            {user?.is_superuser && (
                              <div className="flex gap-1 items-center ml-2 whitespace-nowrap">
                                <button
                                  className="px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
                                  title="Repost (bump to top)"
                                  disabled={!!repostingId}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleRepost(s);
                                  }}
                                >
                                  {repostingId === (s?.id ?? s?._id)
                                    ? "Reposting…"
                                    : "Repost"}
                                </button>
                                <button
                                  className="p-1 rounded hover:bg-white/30"
                                  title="Edit"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openEdit(s);
                                  }}
                                >
                                  <i className="fa-solid text-blue-500 fa-pen"></i>
                                </button>
                                <button
                                  className="p-1 rounded hover:bg-white/30"
                                  title="Delete"
                                  disabled={!!isDeleting}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDelete(s);
                                  }}
                                >
                                  <i className="fa-solid text-red-500 fa-trash"></i>
                                </button>
                              </div>
                            )}
                          </div>

                          <div className="mt-1 text-[9px] opacity-80">
                            {timeAgo(
                              s?.created_at ||
                                s?.createdAt ||
                                s?.updated_at
                            )}
                            <span className={`text-[15px] font-bold float-end `}>
                              {s?.ship_code}- {s?.id}
                            </span>
                          </div>
                        </div>
                      </div>
                    </ul>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* =================== MOBILE BOTTOM SHEET =================== */}
      <div
        className={`hidden xm:block fixed left-0 right-0 z-[1050] transition-all duration-300 ${
          mobileOpen ? "bottom-0" : "bottom-[-340px]"
        }`}
        style={{ height: mobileOpen ? "35vh" : "64px" }}
      >
        <div className="h-full rounded-t-2xl shadow-2xl backdrop-blur-md bg-white/95 dark:bg-slate-900/90 border-t border-slate-200 dark:border-slate-700">
          {/* drag handle / header */}
          <div
            className="relative flex items-center justify-between px-3 py-2 bg-[#253356] text-white rounded-t-2xl"
            onClick={() => setMobileOpen((v) => !v)}
          >
            <div className="absolute left-1/2 -translate-x-1/2 -top-2 w-12 h-1.5 bg-white/70 rounded-full" />
            <div className="font-semibold text-sm">
              {sidebarMode === "nearby" ? "Nearby Ships" : "Recently Listed Ships"}
            </div>
            <button
              className="px-2 py-1 rounded bg-white/15 hover:bg-white/25 text-lg leading-none"
              aria-label="Toggle"
            >
              {mobileOpen ? "▾" : "▴"}
            </button>
          </div>

          {/* controls (search only, Nearby button removed) */}
          <div className="px-3 py-2 flex items-center gap-2">
            <input
              value={sidebarSearch}
              onChange={(e) => setSidebarSearch(e.target.value)}
              placeholder="Search: area, type, DWT…"
              className="flex-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="px-3 pb-1 text-[11px] text-slate-500">
            {loadingShips
              ? "Loading ships…"
              : `Showing ${visibleShipCount} of ${totalShipCount} ships`}
          </div>

          {/* LIST */}
          <div
            ref={mobileListRef}
            className="px-2 pb-2 overflow-y-auto custom-scroll"
            style={{ height: "calc(55vh - 96px)" }}
          >
            {loadingShips ? (
              <div className="text-xs text-slate-500 px-2 py-6 text-center">
                Loading ships…
              </div>
            ) : mobileItems.length === 0 ? (
              <div className="text-xs text-slate-500 px-2 py-6 text-center">
                No ships found.
              </div>
            ) : (
              mobileItems.map(({ ship: s, key }) => {
                const selected = key === selectedShipKey;
                const ownerOpen = ownerOpenKey === key;
                const idStr = String(s?.id ?? s?._id ?? "");
                const isReposting =
                  repostingId && String(repostingId) === idStr;

                return (
                  <button
                    key={`m-side-${key}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedShipKey((k) => {
                        const next = k === key ? null : key;
                        if (next) {
                          const base = getShipAreaPos(s, areaCoords);
                          if (base && mapRef.current && window.google) {
                            const z = Math.max(
                              mapRef.current.getZoom() || 5,
                              6
                            );
                            mapRef.current.panTo(base);
                            mapRef.current.setZoom(z);
                          }
                          setClusterFilterKeys(null);
                          setSidebarMode("nearby");
                        } else {
                          setSidebarMode("recent");
                        }
                        return next;
                      });
                    }}
                    className={`w-full text-left mb-2 p-2 rounded-xl border transition ${
                      selected
                        ? "bg-gray-200"
                        : "bg-gray-50 shadow-lg dark:bg-slate-800 text-slate-800 border-2 border-slate-300 dark:text-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/70"
                    }`}
                  >
                    <div className="text-[13px] font-bold p-2 rounded-t bg-[#d1a460] text-black">
                      SHIP : {s?.vessel_type?.toUpperCase() || "Ship"} FOR
                      CHARTER
                    </div>
                    <ul className="list-disc p-2 list-inside space-y-1">
                      <li className="text-[11px]">
                        <span className="font-semibold">Open Area</span> :{" "}
                        {s?.open_area || "-"}
                      </li>
                      <li className="text-[11px]">
                        <span className="font-semibold">DWT</span> :{" "}
                        {s?.DWT || "-"}
                      </li>
                      <li className="text-[11px]">
                        <span className="font-semibold">Built</span> :{" "}
                        {s?.year_built || "-"}
                      </li>

                      {/* Collapsible details when selected */}
                      <div
                        className="overflow-y-auto transition-[max-height] duration-300 ease-out"
                        style={{ maxHeight: selected ? 220 : 0 }}
                      >
                        <div className="pb-1 text-[11px]">
                          <li>
                            <span className="font-semibold">TC/VC</span>:{" "}
                            {s?.tc_vc || "-"}
                          </li>
                          <li>
                            <span className="font-semibold">Laychan</span>:{" "}
                            {s?.lychan || "-"}
                          </li>
                          <div className="col-span-2">
                            {s?.brief_description
                              ?.split("\n")
                              .map((line, idx) => (
                                <li
                                  key={idx}
                                  className="text-sm text-gray-800 leading-relaxed"
                                >
                                  {line}
                                </li>
                              ))}
                          </div>
                        </div>

                        {/* Owner + Admin row (mobile) */}
                        <div className="mt-1 flex items-center justify-between text-[10px]">
                          <button
                            className={`px-2 py-1 rounded text-white ${
                              isLoggedIn ? "bg-blue-600" : "bg-slate-400"
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!isLoggedIn) {
                                alert(
                                  "Please log in to view owner details."
                                );
                                return;
                              }
                              setOwnerOpenKey((k) =>
                                k === key ? null : key
                              );
                            }}
                          >
                            Owner Info
                          </button>

                          <div className="flex gap-1 items-center">
                            {user?.is_superuser && (
                              <>
                                <button
                                  className="px-2 py-1 rounded bg-emerald-600 text-white disabled:opacity-60"
                                  disabled={!!repostingId}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleRepost(s);
                                  }}
                                >
                                  {repostingId === (s?.id ?? s?._id)
                                    ? "…"
                                    : "Repost"}
                                </button>
                                <button
                                  className="px-2 py-1 rounded bg-white/70"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openEdit(s);
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  className="px-2 py-1 rounded bg-red-600 text-white disabled:opacity-60"
                                  disabled={!!isDeleting}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDelete(s);
                                  }}
                                >
                                  Del
                                </button>
                              </>
                            )}</div>
                        </div>

                        {/* Owner details */}
                        <div
                          className="overflow-hidden transition-[max-height] duration-300 ease-out"
                          style={{
                            maxHeight: isLoggedIn && ownerOpen ? 140 : 0,
                          }}
                        >
                          <div className="mt-2 rounded-lg bg-white/70 dark:bg-slate-700/40 border border-slate-200 dark:border-slate-600 p-2 text-[11px]">
                            <ul className="space-y-1">
                              <li>
                                <span className="font-semibold">Name:</span>{" "}
                                {s?.name || s?.owner_name || "-"}
                              </li>
                              <li>
                                <span className="font-semibold">Email:</span>{" "}
                                {s?.email ? (
                                  <a
                                    href={`mailto:${s.email}`}
                                    className="underline"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {s.email}
                                  </a>
                                ) : (
                                  "-"
                                )}
                              </li>
                              <li>
                                <span className="font-semibold">Phone:</span>{" "}
                                {s?.phone ? (
                                  <a
                                    href={`tel:${s.phone}`}
                                    className="underline"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {s.phone}
                                  </a>
                                ) : (
                                  "-"
                                )}
                              </li>
                              {s?.company && (
                                <li>
                                  <span className="font-semibold">
                                    Company:
                                  </span>{" "}
                                  {s.company}
                                </li>
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="mt-1 text-[9px] opacity-80">
                        {timeAgo(
                          s?.created_at || s?.createdAt || s?.updated_at
                        )}
                        <span className="text-[13px] font-bold float-right">
                          {s?.ship_code}- {s?.id}
                        </span>
                      </div>
                    </ul>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* ===== Edit Ship Modal ===== */}
      {editShip && (
        <div
          className="fixed inset-0 z-[1200] bg-black/40 flex items-center justify-center p-4"
          onClick={() => !savingEdit && setEditShip(null)}
        >
          <div
            className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 bg-[#d1a460] text-black rounded-t-2xl">
              <div className="font-bold">
                Edit Ship • {editShip.ship_code || editShip.id}
              </div>
              <div className="flex items-center gap-2">
                {!!editShip.url_new && (
                  <button
                    onClick={() => window.open(editShip.url_new, "_blank")}
                    className="bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded"
                    title="Open Reference Email"
                  >
                    Ref Email
                  </button>
                )}
                <button
                  onClick={() => !savingEdit && setEditShip(null)}
                  className="px-2 py-1 rounded bg-black/10 hover:bg-black/20"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto">
              <label className="text-sm">
                <span className="block font-semibold mb-1">Vessel Type</span>
                <input
                  value={editShip.vessel_type || ""}
                  onChange={(e) =>
                    setEditShip((s) => ({
                      ...s,
                      vessel_type: e.target.value,
                    }))
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </label>

              <label className="text-sm">
                <span className="block font-semibold mb-1">Open Area</span>
                <input
                  value={editShip.open_area || ""}
                  onChange={(e) =>
                    setEditShip((s) => ({ ...s, open_area: e.target.value }))
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </label>

              <label className="text-sm">
                <span className="block font-semibold mb-1">DWT</span>
                <input
                  value={editShip.DWT || ""}
                  onChange={(e) =>
                    setEditShip((s) => ({ ...s, DWT: e.target.value }))
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </label>

              <label className="text-sm">
                <span className="block font-semibold mb-1">Year Built</span>
                <input
                  value={editShip.year_built || ""}
                  onChange={(e) =>
                    setEditShip((s) => ({
                      ...s,
                      year_built: e.target.value,
                    }))
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </label>

              <label className="text-sm">
                <span className="block font-semibold mb-1">TC/VC</span>
                <input
                  value={editShip.tc_vc || ""}
                  onChange={(e) =>
                    setEditShip((s) => ({ ...s, tc_vc: e.target.value }))
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </label>

              <label className="text-sm">
                <span className="block font-semibold mb-1">Laychan</span>
                <input
                  value={editShip.lychan || ""}
                  onChange={(e) =>
                    setEditShip((s) => ({ ...s, lychan: e.target.value }))
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </label>

              <label className="text-sm md:col-span-2">
                <span className="block font-semibold mb-1">
                  Brief Description
                </span>
                <textarea
                  rows={4}
                  value={editShip.brief_description || ""}
                  onChange={(e) =>
                    setEditShip((s) => ({
                      ...s,
                      brief_description: e.target.value,
                    }))
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </label>

              <label className="text-sm">
                <span className="block font-semibold mb-1">Owner Name</span>
                <input
                  value={editShip.name || editShip.owner_name || ""}
                  onChange={(e) =>
                    setEditShip((s) => ({ ...s, name: e.target.value }))
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </label>

              <label className="text-sm">
                <span className="block font-semibold mb-1">Email</span>
                <input
                  value={editShip.email || ""}
                  onChange={(e) =>
                    setEditShip((s) => ({ ...s, email: e.target.value }))
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </label>

              <label className="text-sm">
                <span className="block font-semibold mb-1">Phone</span>
                <input
                  value={editShip.phone || ""}
                  onChange={(e) =>
                    setEditShip((s) => ({ ...s, phone: e.target.value }))
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </label>

              <label className="text-sm">
                <span className="block font-semibold mb-1">Company</span>
                <input
                  value={editShip.company || ""}
                  onChange={(e) =>
                    setEditShip((s) => ({ ...s, company: e.target.value }))
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </label>

              <label className="text-sm">
                <span className="block font-semibold mb-1">Ship Code</span>
                <input
                  value={editShip.ship_code || ""}
                  onChange={(e) =>
                    setEditShip((s) => ({ ...s, ship_code: e.target.value }))
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </label>
            </div>

            <div className="px-4 py-3 flex items-center justify-end gap-2 border-t">
              <button
                className="px-3 py-2 rounded bg-slate-200 hover:bg-slate-300"
                disabled={savingEdit}
                onClick={() => setEditShip(null)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-60"
                disabled={savingEdit}
                onClick={handleSaveEdit}
              >
                {savingEdit ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-[1200] bg-black/40 flex items-center justify-center">
          <div className="w-[420px] max-w-[90vw] rounded-2xl bg-white dark:bg-slate-800 shadow-2xl border border-slate-200 dark:border-slate-700 p-5">
            <div className="text-base font-semibold mb-2">Delete Ship?</div>
            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-2 rounded bg-slate-200 dark:bg-slate-700"
                onClick={() => setConfirmDel(null)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="px-3 py-2 rounded bg-red-600 text-white disabled:opacity-60"
                onClick={confirmDeleteNow}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Draggable Cargo panel */}
      {selectedCargo && (
        <DraggablePanel
          id="cargo-info"
          initial={{ x: 16, y: 260 }}
          onClose={() => {
            setSelectedCargoKey(null);
            setRoute(null);
            setDest(null);
          }}
          title={`CARGO: ${(selectedCargo.type_of_cargo || "-").toUpperCase()}`}
        >
          <div className="px-4 py-3 max-h-[55vh] overflow-y-auto custom-scroll">
            <ul className="list-disc list-inside space-y-1">
              <li className="text-sm">
                <span className="font-bold">Loading Port:</span>{" "}
                {selectedCargo.loading_port || "N/A"}
              </li>
              <li className="text-sm">
                <span className="font-bold">Discharge Port:</span>{" "}
                {selectedCargo.discharge_port || "N/A"}
              </li>
              <li className="text-sm">
                <span className="font-bold">Lychan:</span>{" "}
                {selectedCargo.lychan || "N/A"}
              </li>
              <li className="text-sm">
                <span className="font-bold">Tonnage:</span>{" "}
                {selectedCargo.tonnage || "N/A"}
              </li>
              <li className="text-sm">
                <span className="font-bold">Commission:</span>{" "}
                {selectedCargo.commission || "N/A"}
              </li>
              {selectedCargo.brief_description
                ?.split("\n")
                .map((line, i) => (
                  <li key={i} className="text-sm leading-relaxed">
                    {line}
                  </li>
                ))}
              <li className="text-sm">
                <span className="font-bold">Charterer Account:</span>{" "}
                {selectedCargo.charterer_account || "N/A"}
              </li>
            </ul>
            <button
              className="mt-3 w-full bg-[#25D366] hover:opacity-90 text-white font-semibold py-2 px-3 rounded"
              onClick={(e) => {
                e.preventDefault();
                const key = getCargoKey(selectedCargo);
                shareCargoOnWhatsApp(key, selectedCargo);
              }}
            >
              Share via WhatsApp
            </button>
          </div>
        </DraggablePanel>
      )}

      {/* Top-left overlay */}
      <div className="absolute left-[340px] xm:top-40   xm:left-2 top-44 z-[1000] pointer-events-none">
        <div className="flex gap-3 items-center pointer-events-auto ">
          <button
            onClick={handleViewList}
            className="bg-[#d1a460] p-2 rounded-lg text-gray-50 "
          >
            View List
          </button>
        </div>
      </div>

      {/* Top-right overlay: ships loaded badge + basemap selector */}
      <div className="absolute xm:float-right xm:flex-col xm:gap-2 flex items-center gap-5 right-14 top-44 z-[1000]">
        {/* SHIPS COUNT BADGE with spinner / tick */}
        {shipsLoadedPercentage < 100 && (
          <div className="z-[1100] bg-[#123d5f] py-2 text-white dark:bg-slate-900/95 border border-slate-200 dark:border-slate-700 shadow-lg rounded-full px-3 text-[12px] font-medium dark:text-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-5 h-4 flex items-center justify-center rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900">
                {shipsLoadedPercentage < 100 ? (
                  <div className="w-3 h-3 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                ) : (
                  <span className="text-xs text-green-600">✓</span>
                )}
              </div>
              <span className="text-sm">
                {shipsLoadedPercentage < 100
                  ? `Loading ships ${shipsLoadedPercentage}%`
                  : "All ships loaded"}
              </span>
            </div>
          </div>
        )}

        <label htmlFor="basemap-select" className="sr-only">
          Base map
        </label>
        <select
          id="basemap-select"
          value={activeMapType}
          onChange={(e) => setActiveMapType(e.target.value)}
          className="w-24 xm:float-end py-2 text-sm rounded-md bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-100 border border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="roadmap">Light</option>
          <option value="dark">Dark</option>
          <option value="hybrid">Satellite</option>
        </select>
      </div>

      {/* Bottom-right controls */}
      <div className="absolute   xm:flex-col xm:justify-right  xm:ml-auto  right-2 bottom-2 z-[1000] flex gap-2 pointer-events-auto">
        {/* <select
          value={activeMapType}
          onChange={(e) => setActiveMapType(e.target.value)}
          className="bg-white/90 border rounded-lg px-2 xm:w-20 xm:ml-auto  py-2 text-sm"
          title="Basemap"
        >
          <option value="roadmap">Light</option>
          <option value="dark">Dark</option>
          <option value="hybrid">Satellite</option>
        </select> */}

        <button
          onClick={() => {
            setShowCargos((v) => {
              const next = !v;
              if (!next) {
                setSelectedCargoKey(null);
                setRoute(null);
                setDest(null);
              }
              return next;
            });
          }}
          className="bg-[#253356] xm:w-[200px] hover:bg-[#1b2a4f] text-white font-semibold px-3 py-2 rounded-xl shadow-lg disabled:opacity-60"
          aria-pressed={showCargos ? "true" : "false"}
          aria-label={showCargos ? "Hide cargo" : "View cargo available"}
          disabled={loadingCargos && !showCargos}
        >
          {showCargos
            ? "Hide Cargo"
            : loadingCargos
            ? "Loading Cargo…"
            : "View Cargo Available"}
        </button>
      </div>

      {/* ============================ GOOGLE MAP ============================ */}
      <GoogleMap
        onLoad={(map) => {
          mapRef.current = map;
          const didFly = sessionStorage.getItem(DID_FLY_KEY) === "1";
          if (!didFly && userPos) {
            sessionStorage.setItem(DID_FLY_KEY, "1");
            map.panTo(userPos);
            map.setZoom(Math.max(map.getZoom() || 5, 7));
          }
        }}
        onUnmount={() => (mapRef.current = null)}
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={userPos || INITIAL_CENTER}
        zoom={5}
        options={mapOptions}
        onClick={() => clearSelection()}
        onZoomChanged={() => {
          if (!mapRef.current) return;
          const z = mapRef.current.getZoom() || 5;
          setMapZoom(z);
        }}
        onMapTypeIdChanged={() => {
          if (!mapRef.current) return;
          const t = mapRef.current.getMapTypeId();
          setActiveMapType(t);
        }}
      >
        {/* User dot */}
        {userPos && (
          <MarkerF
            position={userPos}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              fillColor: "#0ea5e9",
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 2,
              scale: 6,
            }}
            zIndex={1000}
          />
        )}

        {/* Ships (clustered when zoomed out) */}
        {shipMarkers}

        {/* Cargos */}
        {showCargos && cargoMarkers}

        {/* Route */}
        {Array.isArray(route) && route.length >= 2 && (
          <PolylineF
            path={route}
            options={{
              strokeOpacity: 1,
              strokeWeight: 3,
              strokeColor:
                activeMapType === "hybrid"
                  ? "#F59E0B"
                  : activeMapType === "dark"
                  ? "#2563EB"
                  : activeMapType === "roadmap"
                  ? "#2563EB"
                  : "#CFFF04",
            }}
          />
        )}

        {/* Destination pin */}
        {dest && (
  <MarkerF
    position={dest}
    icon={{
      url: `data:image/svg+xml;utf8,${encodeURIComponent(
        `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C8.14 2 5 5.14 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.86-3.14-7-7-7z" fill="#ef4444"/><circle cx="12" cy="9" r="3.2" fill="#fff"/></svg>`
      )}`,
      scaledSize: { width: 34, height: 34 },
      anchor: { x: 17, y: 30 },
    }}
    zIndex={950}
  />
)}

      </GoogleMap>

      <style>{`
        .custom-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
        .custom-scroll::-webkit-scrollbar-thumb { background: rgba(100,116,139,.35); border-radius: 999px; }
        .custom-scroll::-webkit-scrollbar-track { background: transparent; }
      `}</style>
    </div>
  );
}
